// gameApi.ts
// API client for WordUpmanship backend

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

class GameApi {
  
  /**
   * Get today's seed word
   */
  async getDailySeed(): Promise<{ seedWord: string; date: string }> {
    const response = await fetch(`${API_URL}/api/daily-seed`);
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to get daily seed');
    }
    
    return {
      seedWord: data.seedWord,
      date: data.date
    };
  }

  /**
   * Create a new game
   */
  async createGame(): Promise<{
    seedWord: string;
    board: (string | null)[][];
    positionStreaks: any;
  }> {
    const response = await fetch(`${API_URL}/api/game/new`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to create game');
    }
    
    return {
      seedWord: data.seedWord,
      board: data.board,
      positionStreaks: data.positionStreaks
    };
  }

  /**
   * Validate word placement
   */
  async validateWord(params: {
    word: string;
    row: number;
    startCol?: number; // ← ADD THIS
    board: (string | null)[][];
    usedWords: string[];
  }): Promise<{ placements: any[] }> {
    const response = await fetch(`${API_URL}/api/game/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Word validation failed');
    }
    
    return { placements: data.placements };
  }

  /**
   * Calculate score for a word placement
   */
  async calculateScore(params: {
    word: string;
    row: number;
    startCol: number;
    matchLength: number;
    positionStreaks: any;
    board: (string | null)[][];
  }): Promise<{
    baseScore: number;
    finalScore: number;
    newStreaks: any;
    streakBonuses: any[];
  }> {
    const response = await fetch(`${API_URL}/api/game/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Score calculation failed');
    }
    
    return {
      baseScore: data.baseScore,
      finalScore: data.finalScore,
      newStreaks: data.newStreaks,
      streakBonuses: data.streakBonuses
    };
  }

  /**
   * Submit final game score (silent auto-submit)
   */
  async submitScore(params: {
    seedWord: string;
    seedDate: string;
    score: number;
  }): Promise<void> {
    const response = await fetch(`${API_URL}/api/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to submit score');
    }
  }

  /**
   * Get today's highest score
   */
  async getTodaysHigh(date?: string): Promise<number | null> {
    const url = date 
      ? `${API_URL}/api/high-score/today?date=${date}`
      : `${API_URL}/api/high-score/today`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch high score');
    }
    
    return data.highScore;
  }
}

export default new GameApi();
